import { Message } from '../components/TalkToTallyV2Project/types';
import { WorkspaceCard } from '../components/TalkToTallyV2Project/components/Workspace/WorkspacePanel';
import { getAuthHeaders, getBaseUrl } from './apiClient';

export interface ConversationData {
  userId: string;
  sessionId?: string;
  createdAt: string;
  topic: string;
  title?: string;
  description?: string;
  topicCategory?: string;
  duration: number;
  transcript: Message[];
  workspaceItems: WorkspaceCard[];
  skillAssessment?: any;
  aiVoice: string;
  userLevel?: string;
  promptMode?: string;
  promptId?: string;
}

export interface ConversationSummary {
  userId: string;
  sessionId: string;
  createdAt: string;
  topic: string;
  duration: number;
  messageCount: number;
  hasAssessment: boolean;
  workspaceItemCount: number;
  metadata: any;
}

export interface ConversationListResponse {
  sessions: ConversationSummary[];
  count: number;
  lastEvaluatedKey?: string;
}

export const conversationService = {
  storeConversation: async (conversationData: ConversationData): Promise<{ sessionId: string }> => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}/secure/tally-conversations`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(conversationData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to store conversation: ${response.status}`);
      }

      const result = await response.json();
      return { sessionId: result.sessionId };
    } catch (error) {
      console.error('Failed to store conversation:', error);
      throw error;
    }
  },

  getConversations: async (
    userId: string, 
    options?: {
      limit?: number;
      startDate?: string;
      endDate?: string;
      lastEvaluatedKey?: string;
    }
  ): Promise<ConversationListResponse> => {
    const baseUrl = getBaseUrl();
    
    const params = new URLSearchParams({ userId });
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.lastEvaluatedKey) params.append('lastEvaluatedKey', options.lastEvaluatedKey);
    
    const url = `${baseUrl}/secure/tally-conversations?${params.toString()}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: await getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to retrieve conversations: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to retrieve conversations:', error);
      throw error;
    }
  },

  getConversation: async (userId: string, sessionId: string): Promise<ConversationData> => {
    const baseUrl = getBaseUrl();
    
    const url = `${baseUrl}/secure/tally-conversations/${sessionId}?userId=${encodeURIComponent(userId)}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: await getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to retrieve conversation: ${response.status}`);
      }

      const result = await response.json();
      return result.session;
    } catch (error) {
      console.error('Failed to retrieve conversation:', error);
      throw error;
    }
  }
};